import { useState, useCallback, useMemo } from 'react';
import { useUser } from '@/features/auth';
import { useEmploymentRole } from '@/features/auth';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { DOCK_APPS, DEFAULT_DOCK_IDS, filterByPermissions } from './dockRegistry';
import type { DockApp } from './dockRegistry';

const STORAGE_KEY = 'brainsway_dock_config';
const CONFIG_VERSION = 3; // bump when registry IDs change
const MAX_DOCK_ITEMS = 10;

interface DockConfig {
  version: number;
  order: string[];
  hidden: string[];
}

function loadConfig(userId: string): DockConfig | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const allConfigs = JSON.parse(stored);
    const cfg = allConfigs[userId] ?? null;
    if (!cfg || cfg.version !== CONFIG_VERSION) return null; // stale → reset
    return cfg;
  } catch {
    return null;
  }
}

function saveConfig(userId: string, config: DockConfig): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const allConfigs = stored ? JSON.parse(stored) : {};
    allConfigs[userId] = config;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allConfigs));
  } catch {
    // ignore storage errors
  }
}

function buildDefaults(permittedApps: DockApp[], permittedIds: Set<string>): DockConfig {
  const defaultOrder = DEFAULT_DOCK_IDS.filter((id) => permittedIds.has(id));
  const hidden = permittedApps
    .filter((a) => !defaultOrder.includes(a.id))
    .map((a) => a.id);
  return { version: CONFIG_VERSION, order: defaultOrder, hidden };
}

export function useDockStore() {
  const user = useUser();
  const { canCreateReports } = useEmploymentRole();
  const { hasPermission } = usePermissions();
  const userId = user?.id ?? 'anonymous';

  const permittedApps = useMemo(
    () => filterByPermissions(DOCK_APPS, { canCreateReports, hasPermission }),
    [canCreateReports, hasPermission],
  );

  const permittedIds = useMemo(() => new Set(permittedApps.map((a) => a.id)), [permittedApps]);

  const [config, setConfig] = useState<DockConfig>(() => {
    const saved = loadConfig(userId);
    if (saved) return saved;
    return buildDefaults(permittedApps, permittedIds);
  });

  const persist = useCallback(
    (next: DockConfig) => {
      setConfig(next);
      saveConfig(userId, next);
    },
    [userId],
  );

  // Active apps: ordered, permitted, not hidden
  const activeApps = useMemo(() => {
    const hiddenSet = new Set(config.hidden);
    const ordered: DockApp[] = [];

    for (const id of config.order) {
      if (permittedIds.has(id) && !hiddenSet.has(id)) {
        const app = DOCK_APPS.find((a) => a.id === id);
        if (app) ordered.push(app);
      }
    }

    return ordered;
  }, [config, permittedIds]);

  // Available apps: permitted but not active (can be added via + button)
  const availableApps = useMemo(() => {
    const activeIds = new Set(activeApps.map((a) => a.id));
    return permittedApps.filter((a) => !activeIds.has(a.id));
  }, [activeApps, permittedApps]);

  const reorder = useCallback(
    (newOrder: string[]) => {
      persist({ ...config, order: newOrder });
    },
    [config, persist],
  );

  const removeApp = useCallback(
    (id: string) => {
      persist({
        ...config,
        order: config.order.filter((i) => i !== id),
        hidden: [...config.hidden, id],
      });
    },
    [config, persist],
  );

  const isFull = activeApps.length >= MAX_DOCK_ITEMS;

  const addApp = useCallback(
    (id: string) => {
      if (activeApps.length >= MAX_DOCK_ITEMS) return;
      persist({
        ...config,
        order: [...config.order.filter((i) => i !== id), id],
        hidden: config.hidden.filter((i) => i !== id),
      });
    },
    [config, persist, activeApps.length],
  );

  const resetToDefaults = useCallback(() => {
    persist(buildDefaults(permittedApps, permittedIds));
  }, [permittedApps, permittedIds, persist]);

  return { activeApps, availableApps, isFull, reorder, addApp, removeApp, resetToDefaults };
}
